import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "";

const PLAN_LABELS = {
  trial:      "Free Trial",
  solo:       "Solo",
  team:       "Team",
  business:   "Business",
  enterprise: "Enterprise",
};

const STATUS_LABELS = {
  trial:     "Trial",
  active:    "Active",
  expired:   "Expired",
  cancelled: "Cancelled",
};

export default function BillingPage() {
  const { token, activeCompany } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  const [interval, setInterval] = useState("monthly");

  useEffect(() => {
    fetch(`${API}/billing/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(data => { setStatus(data); setLoading(false); })
      .catch(() => { setError("Could not load billing information."); setLoading(false); });
  }, [token]);

  function handleCheckout() {
    if (!activeCompany?.id) return;
    setCheckoutLoading(true);
    fetch(`${API}/billing/checkout`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ plan: interval === "yearly" ? "solo_yearly" : "solo", company_id: activeCompany.id }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(data => {
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          setError("Could not start checkout. Please try again.");
          setCheckoutLoading(false);
        }
      })
      .catch(() => {
        setError("Could not start checkout. Please try again.");
        setCheckoutLoading(false);
      });
  }

  const cardStyle = {
    background: "white",
    borderRadius: 12,
    padding: "28px 32px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    marginBottom: 24,
  };

  if (loading) return (
    <div style={{ padding: 40, color: "#6b8cae" }}>Loading billing information…</div>
  );

  if (error) return (
    <div style={{ padding: 40, color: "#c0392b" }}>{error}</div>
  );

  const isTrial   = status?.subscription_status === "trial";
  const isExpired = status?.subscription_status === "expired";
  const isActive  = status?.subscription_status === "active";
  const daysLeft  = status?.trial_days_remaining;

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: "#1e3a5f", marginBottom: 24 }}>
        Billing & Subscription
      </h1>

      {/* Current status card */}
      <div style={cardStyle}>
        <div style={{ fontSize: 13, color: "#6b8cae", marginBottom: 6 }}>Current plan</div>
        <div style={{ fontSize: 20, fontWeight: 600, color: "#1e3a5f", marginBottom: 16 }}>
          {PLAN_LABELS[status?.plan] || status?.plan}
        </div>

        <div style={{ display: "flex", gap: 32 }}>
          <div>
            <div style={{ fontSize: 12, color: "#6b8cae", marginBottom: 2 }}>Status</div>
            <div style={{
              display: "inline-block",
              padding: "3px 10px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 500,
              background: isActive ? "#e6f4ea" : isTrial ? "#fff8e1" : "#fdecea",
              color: isActive ? "#2e7d32" : isTrial ? "#e65100" : "#c0392b",
            }}>
              {STATUS_LABELS[status?.subscription_status] || status?.subscription_status}
            </div>
          </div>

          {isTrial && daysLeft !== null && (
            <div>
              <div style={{ fontSize: 12, color: "#6b8cae", marginBottom: 2 }}>Trial ends in</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: daysLeft <= 3 ? "#c0392b" : "#1e3a5f" }}>
                {daysLeft === 0 ? "Today" : `${daysLeft} day${daysLeft !== 1 ? "s" : ""}`}
              </div>
            </div>
          )}

          {isActive && status?.subscription_expires_at && (
            <div>
              <div style={{ fontSize: 12, color: "#6b8cae", marginBottom: 2 }}>Renews</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#1e3a5f" }}>
                {new Date(status.subscription_expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade / subscribe card — only show if not active */}
      {!isActive && (
        <>
        {/* Interval toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button
            onClick={() => setInterval("monthly")}
            style={{
              padding: "6px 16px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer", border: "none",
              background: interval === "monthly" ? "#1e3a5f" : "#e8eef5",
              color: interval === "monthly" ? "white" : "#6b8cae",
            }}
          >Monthly</button>
          <button
            onClick={() => setInterval("yearly")}
            style={{
              padding: "6px 16px", borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: "pointer", border: "none",
              background: interval === "yearly" ? "#1e3a5f" : "#e8eef5",
              color: interval === "yearly" ? "white" : "#6b8cae",
            }}
          >Yearly</button>
          {interval === "yearly" && (
            <span style={{ fontSize: 12, color: "#2e7d32", fontWeight: 500, background: "#e6f4ea", padding: "3px 10px", borderRadius: 20 }}>
              Save €58 excl. BTW
            </span>
          )}
        </div>
          <div style={{ marginBottom: 16 }}>
            <a href="/plans" style={{ fontSize: 13, color: "#1e7fc7", textDecoration: "none" }}>
              Compare all plans &rarr;
            </a>
          </div>

        {/* Solo plan card */}
        <div style={{ ...cardStyle, border: "1px solid #b8d4f0" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", marginBottom: 4 }}>
            Solo plan
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#1e3a5f", marginBottom: 4 }}>
            {interval === "monthly" ? "€56" : "€560"}
            <span style={{ fontSize: 14, fontWeight: 400, color: "#6b8cae" }}>
              {interval === "monthly" ? "/month" : "/year"}
            </span>
          </div>
          {interval === "monthly" && (
            <div style={{ fontSize: 12, color: "#6b8cae", marginBottom: 8 }}>
              or €560/year — save €112
            </div>
          )}
          {interval === "yearly" && (
            <div style={{ fontSize: 12, color: "#6b8cae", marginBottom: 8 }}>
              billed annually — equivalent to €46.67/month
            </div>
          )}
          <div style={{ fontSize: 11, color: "#94afc9", marginBottom: 16 }}>
            Excl. 21% BTW · incl. BTW: {interval === "monthly" ? "€67.76/mo" : "€677.60/yr"}
          </div>
          <div style={{ fontSize: 13, color: "#6b8cae", marginBottom: 20, lineHeight: 1.6 }}>
            Unlimited inspections · PDF reports · Evidence workflow · Open point tracking
          </div>
          <button
            onClick={handleCheckout}
            disabled={checkoutLoading}
            style={{
              background: checkoutLoading ? "#a0b4c8" : "#1e3a5f",
              color: "white", border: "none", borderRadius: 8,
              padding: "10px 24px", fontSize: 14, fontWeight: 500,
              cursor: checkoutLoading ? "not-allowed" : "pointer",
            }}
          >
            {checkoutLoading ? "Redirecting…" : isExpired ? "Reactivate subscription" : "Subscribe now"}
          </button>
          <div style={{ marginTop: 12, fontSize: 12, color: "#6b8cae" }}>
            Secure payment via Mollie. You will be redirected to complete payment.
          </div>
        </div>
        </>
      )}

      {/* Enterprise */}
      <div style={{ ...cardStyle, background: "#f8fafc" }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: "#1e3a5f", marginBottom: 6 }}>
          Need Team, Business or Enterprise?
        </div>
        <div style={{ fontSize: 13, color: "#6b8cae", marginBottom: 12, lineHeight: 1.6 }}>
          Team (€386/mo) and Business (€1273/mo) plans are coming soon. Enterprise pricing is custom.
        </div>

          <a href="mailto:info@oskar-india-tango.app"
          style={{ fontSize: 13, color: "#1e7fc7", textDecoration: "none", fontWeight: 500 }} >
          Contact us &rarr; info@oskar-india-tango.app
        </a>
      </div>
      <div style={{ fontSize: 12, color: "#6b8cae", marginTop: 8, textAlign: "center" }}>
        By subscribing you agree to our{" "}
        <a href="/terms" style={{ color: "#1e7fc7" }}>Terms and Conditions</a>
        {" "}and{" "}
        <a href="/privacy" style={{ color: "#1e7fc7" }}>Privacy Policy</a>.
      </div>
    </div>
  );
}